/**
 * Utility functions for extracting inventory items from form payloads
 * and converting them to Offer/Invoice positions
 */
import { IDueInventoryItemConfig, readDueInventoryItemConfig } from "./dueInventoryItem";
import { dueStatus } from "./monthYear";
import {
    alignTableRows,
    IAlignmentReport,
    parseTableRows,
    toAlignedColumns,
} from "./tableRowAligner";

export interface IExtractedInventoryItem {
    id?: number | string; // Database ID (optional)
    clientUuid?: string | null;
    shortName: string;
    name: string; // Full name of the inventory item
    description: string; // Description of the inventory item
    salesPrice: number; // Sales price in cents or main currency unit
    count: number;
    totalQty: number;
    sources: Array<{
        formIndex: number;
        fieldType: string;
        fieldHashCode: string;
        tableRowIndex?: number;
        due?: string;
    }>;
    tableRowHints: string[]; // Hints like "Tür 1", "Tür 2" for table items
    formType?: string;
}

export interface IFormPayload {
    fields?: IFormField[];
    entryName?: string;
    form?: {
        name?: string;
        endpoint?: string;
    };
}

export interface IFormField {
    type: string;
    name?: string;
    hashCode?: string;
    label?: string;
    subfields?: IFormField[];
    value?: any;
    initialValue?: any;
    [key: string]: any;
}

export interface IInvoicePosition {
    nr: number;
    title: string;
    description: string;
    qty: number;
    unit: "repair" | "service" | "disposal" | "hours";
    price: number;
    inventory_item_id: number | null;
    inventory_item_client_uuid?: string | null;
    in_stock: boolean;
    inventory_item_missing?: boolean;
    userModified?: boolean;
}

export interface ITableAlignment {
    tableLabel: string;
    report: IAlignmentReport;
}

export interface IExtractedItemsResult {
    items: IExtractedInventoryItem[];
    totalCount: number;
    formLocations?: string[];
    formEntryNames?: string[];
    formProtocolLabels?: string[];
    formTypeOrder?: string[];
    alignments: ITableAlignment[];
}

export function isTurListeFormName(formName?: string): boolean {
    const name = (formName || "").toLowerCase();
    return name.indexOf("türliste") !== -1 || name.indexOf("tuerliste") !== -1;
}

/**
 * Resolves the schema fields from a form_revision.form_data value.
 * Handles both the flat shape ({"fields":[...]}, optionally JSON-encoded)
 * and the legacy shape where a serialized form object nests the fields
 * under latest_revision.form_data (itself a JSON string).
 */
export function parseSchemaFields(rawFormData: any): IFormField[] {
    let data: any = rawFormData;
    for (let depth = 0; depth < 6 && data; depth++) {
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch (e) {
                return [];
            }
            continue;
        }
        if (typeof data !== "object") {
            return [];
        }
        if (Array.isArray(data.fields)) {
            return data.fields;
        }
        data = data.latest_revision && data.latest_revision.form_data;
    }
    return [];
}

/**
 * Recursively finds all fields of specific types in a form field tree
 */
function findFieldsByType(
    fields: IFormField[],
    targetTypes: string[],
    parentPath: string = "",
    results: Array<{ field: IFormField; path: string }> = []
): Array<{ field: IFormField; path: string }> {
    for (const field of fields) {
        const currentPath = parentPath
            ? `${parentPath}.${field.hashCode || field.name || "unknown"}`
            : field.hashCode || field.name || "unknown";

        if (targetTypes.includes(field.type)) {
            results.push({ field, path: currentPath });
        }

        // Recursively search in subfields (GroupField, TableField, etc.)
        if (field.subfields && Array.isArray(field.subfields)) {
            findFieldsByType(field.subfields, targetTypes, currentPath, results);
        }
    }

    return results;
}

/**
 * Parses inventory item values from a field
 * Supports both array format and string format (comma-separated)
 */
function parseInventoryItemValues(value: any): string[] {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.filter((v) => typeof v === "string" && v.trim() !== "");
    }

    if (typeof value === "string") {
        return value
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v !== "");
    }

    return [];
}

function numericInventoryId(id: any): number | null {
    if (typeof id === "number" && Number.isFinite(id)) {
        return id;
    }
    if (typeof id === "string" && /^\d+$/.test(id)) {
        return parseInt(id, 10);
    }
    return null;
}

function clientUuidOf(cellData: any): string | null {
    if (typeof cellData?.client_uuid === "string" && cellData.client_uuid) {
        return cellData.client_uuid;
    }
    if (
        typeof cellData?.id === "string" &&
        cellData.id &&
        numericInventoryId(cellData.id) === null
    ) {
        return cellData.id;
    }
    return null;
}

/**
 * Parses table data from a TableField
 * Table data is stored as a 2D array where each row contains column objects
 */
function parseTableData(tableField: IFormField): any[][] {
    return parseTableRows(tableField.value) || [];
}

/**
 * Extracts inventory items from InventoryItemSelectTableRow within a TableField
 * Returns array of { shortName, name, description, salesPrice, rowIndex, columnValue }
 */
function extractItemsFromTable(
    tableField: IFormField,
    formIndex: number,
    alignments: ITableAlignment[]
): IExtractedInventoryItem[] {
    const items: IExtractedInventoryItem[] = [];
    const rawTableData = parseTableData(tableField);

    if (!rawTableData || !Array.isArray(rawTableData)) {
        return items;
    }

    // Find the column index for InventoryItemSelectTableRow
    const subfields = tableField.subfields || [];
    const inventoryItemColumnIndex = subfields.findIndex(
        (subfield) => subfield.type === "InventoryItemSelectTableRow"
    );

    if (inventoryItemColumnIndex === -1) {
        return items;
    }

    const { rows: tableData, report } = alignTableRows(rawTableData, toAlignedColumns(subfields));

    // Iterate through table rows
    tableData.forEach((row, rowIndex) => {
        if (!Array.isArray(row)) {
            return;
        }

        const columnData = row[inventoryItemColumnIndex];
        if (!columnData) {
            return;
        }

        // Column data can have value as array of {label, value, cell} objects or single value
        // The full inventory item data is in the .cell property
        const rawValues = columnData.value;
        const values = Array.isArray(rawValues) ? rawValues : rawValues ? [rawValues] : [];

        values.forEach((val: any) => {
            if (!val) {
                return;
            }
            if (typeof val === "string") {
                items.push({
                    id: null,
                    shortName: val,
                    name: val,
                    description: "",
                    salesPrice: 0,
                    count: 1,
                    totalQty: 1,
                    sources: [
                        {
                            formIndex,
                            fieldType: "InventoryItemSelectTableRow",
                            fieldHashCode: tableField.hashCode || "",
                            tableRowIndex: rowIndex,
                        },
                    ],
                    tableRowHints: [`Tür ${rowIndex + 1}`],
                });
                return;
            }

            const cellData = val?.cell || val;
            const shortName = cellData?.short_name || cellData?.value || cellData?.label || "";
            const name = cellData?.name || shortName;
            const description = cellData?.description || "";
            const salesPrice = typeof cellData?.sales_price === "number" ? cellData.sales_price : 0;

            if (shortName) {
                items.push({
                    id: numericInventoryId(cellData?.id),
                    clientUuid: clientUuidOf(cellData),
                    shortName,
                    name,
                    description,
                    salesPrice,
                    count: 1,
                    totalQty: 1,
                    sources: [
                        {
                            formIndex,
                            fieldType: "InventoryItemSelectTableRow",
                            fieldHashCode: tableField.hashCode || "",
                            tableRowIndex: rowIndex,
                        },
                    ],
                    tableRowHints: [`Tür ${rowIndex + 1}`],
                });
            }
        });
    });

    if (report.rowsShifted > 0 || report.unmatchedCells > 0) {
        alignments.push({
            tableLabel: tableField.label || tableField.hashCode || "",
            report,
        });
    }

    return items;
}

interface IDueHit {
    config: IDueInventoryItemConfig;
    fieldHashCode: string;
    label: string;
    tableRowIndex?: number;
    manufactured: string;
    expires: string;
}

function dueConfigOf(field: IFormField): IDueInventoryItemConfig | null {
    return readDueInventoryItemConfig(
        (field.rawOptions && field.rawOptions.dueInventoryItem) ||
            (field.options && field.options.dueInventoryItem)
    );
}

function plainLabel(label: any): string {
    return String(label || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function conditionValue(field: IFormField): any {
    if (field.type === "CheckboxField") {
        return field.value === true || field.value === "on";
    }
    if (field.type === "RadioField") {
        return resolveFieldDisplayValue(field);
    }
    return field.value == null ? "" : field.value;
}

function ruleMatches(rule: any, value: any): boolean {
    switch (String(rule.operator || "").toUpperCase()) {
        case "=":
            return value === rule.value;
        case "<":
            return value.length < rule.value;
        case ">":
            return value.length > rule.value;
        case "<=":
            return value.length <= rule.value;
        case ">=":
            return value.length >= rule.value;
        case "IS NULL":
            return !value;
        case "IS NOT NULL":
            return !!value;
        case "IN":
            return (
                Array.from(rule.value).indexOf(value) > -1 ||
                Array.from(value).indexOf(rule.value) > -1
            );
        case "NOT IN":
            return (
                Array.from(rule.value).indexOf(value) === -1 &&
                Array.from(value).indexOf(rule.value) === -1
            );
        default:
            return false;
    }
}

export function isShownByCondition(field: IFormField, rootFields: IFormField[]): boolean {
    const conditional =
        (field.rawOptions && field.rawOptions.conditional) ||
        (field.options && field.options.conditional);
    const ruleSet = conditional && conditional.data;
    if (
        !ruleSet ||
        conditional.query === "()" ||
        !Array.isArray(ruleSet.rules) ||
        ruleSet.rules.length === 0
    ) {
        return true;
    }
    const results: boolean[] = [];
    ruleSet.rules.forEach((rule: any) => {
        const companion = findFieldByHashCode(rootFields, rule.field);
        if (companion) {
            results.push(ruleMatches(rule, conditionValue(companion)));
        }
    });
    if (results.length === 0) {
        return false;
    }
    if (ruleSet.combinator === "OR") {
        return results.some((result) => result);
    }
    if (ruleSet.combinator === "NOT") {
        return false;
    }
    return results.every((result) => result);
}

function dueHit(
    value: any,
    config: IDueInventoryItemConfig,
    referenceDate: Date,
    hit: { fieldHashCode: string; label: string; tableRowIndex?: number }
): IDueHit | null {
    const status = dueStatus(value, config.lifetimeYears, referenceDate);
    if (!status || !status.offered) {
        return null;
    }
    return { config, ...hit, manufactured: status.manufactured, expires: status.expires };
}

function collectDueHits(
    fields: IFormField[],
    referenceDate: Date,
    rootFields: IFormField[] = fields,
    hits: IDueHit[] = []
): IDueHit[] {
    for (const field of fields) {
        if (!isShownByCondition(field, rootFields)) {
            continue;
        }
        if (field.type === "TableField") {
            const subfields = field.subfields || [];
            const dueColumns = subfields
                .map((subfield, index) => ({ index, subfield, config: dueConfigOf(subfield) }))
                .filter((column) => !!column.config);
            if (dueColumns.length === 0) {
                continue;
            }
            const { rows } = alignTableRows(parseTableData(field), toAlignedColumns(subfields));
            rows.forEach((row, rowIndex) => {
                if (!Array.isArray(row)) {
                    return;
                }
                dueColumns.forEach(({ index, subfield, config }) => {
                    const cell = row[index];
                    const cellHit =
                        cell &&
                        dueHit(cell.value, config!, referenceDate, {
                            fieldHashCode: subfield.hashCode || "",
                            label: plainLabel(subfield.label),
                            tableRowIndex: rowIndex,
                        });
                    if (cellHit) {
                        hits.push(cellHit);
                    }
                });
            });
            continue;
        }

        const fieldConfig = dueConfigOf(field);
        const fieldHit =
            fieldConfig &&
            dueHit(field.value, fieldConfig, referenceDate, {
                fieldHashCode: field.hashCode || "",
                label: plainLabel(field.label),
            });
        if (fieldHit) {
            hits.push(fieldHit);
        }

        if (field.subfields && Array.isArray(field.subfields)) {
            collectDueHits(field.subfields, referenceDate, rootFields, hits);
        }
    }
    return hits;
}

export function dueNotesForPayload(
    payload: IFormPayload,
    referenceDate: Date = new Date()
): string[] {
    return collectDueHits(payload.fields || [], referenceDate).map((hit) => {
        const where = [hit.label, hit.tableRowIndex == null ? "" : `Zeile ${hit.tableRowIndex + 1}`]
            .filter((part) => part !== "")
            .join(" ");
        const what = hit.config.name || hit.config.shortName || String(hit.config.inventoryItemId);
        return `Fällig: ${where} hergestellt ${hit.manufactured}, Austausch ${hit.expires} (${what})`;
    });
}

function findFieldByHashCode(fields: IFormField[], hashCode: string): IFormField | undefined {
    for (const field of fields) {
        if (field.hashCode === hashCode) {
            return field;
        }
        if (field.subfields && Array.isArray(field.subfields)) {
            const found = findFieldByHashCode(field.subfields, hashCode);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}

function isBlankValueString(value: string): boolean {
    return value === "" || value === "null" || value === "undefined" || value === "NaN";
}

function entryToLabel(entry: any, items?: any[]): string {
    if (entry == null) {
        return "";
    }
    if (typeof entry === "object") {
        if (entry.label != null && String(entry.label).trim() !== "") {
            return String(entry.label).trim();
        }
        const id = entry.value != null ? entry.value : entry.id;
        if (id != null) {
            const item = Array.isArray(items)
                ? items.find((it: any) => it.id === id || it.name === id)
                : undefined;
            return item ? String(item.name) : String(id);
        }
        return "";
    }
    const match = Array.isArray(items)
        ? items.find((it: any) => it.id === entry || it.name === entry)
        : undefined;
    return match ? String(match.name) : String(entry);
}

function resolveFieldDisplayValue(field?: IFormField): string {
    if (!field) {
        return "";
    }
    let value: any = field.value != null ? field.value : field.initialValue;
    if (value == null) {
        return "";
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (isBlankValueString(trimmed)) {
            return "";
        }
        if (trimmed[0] === "[" || trimmed[0] === "{") {
            try {
                value = JSON.parse(trimmed);
            } catch (e) {
                return trimmed;
            }
        } else {
            value = trimmed;
        }
    }

    if (value == null) {
        return "";
    }

    const entries = Array.isArray(value) ? value : [value];
    return entries
        .map((entry) => entryToLabel(entry, field.items))
        .filter((name) => name && !isBlankValueString(name.trim()))
        .join(", ");
}

function getProtocolLocationLabel(payload: IFormPayload): string {
    const fields = payload.fields || [];
    const stockwerk = resolveFieldDisplayValue(findFieldByHashCode(fields, "stockwerk"));
    const standort = resolveFieldDisplayValue(findFieldByHashCode(fields, "entity_location"));
    return [stockwerk, standort]
        .filter((s) => s && s.trim() !== "")
        .join(" ")
        .trim();
}

export function formTypeLabel(formName?: string): string {
    return (formName || "").replace(/^\d+_/, "").trim();
}

function getProtocolShortLabel(payload: IFormPayload): string {
    const fields = payload.fields || [];
    const rpnummer = resolveFieldDisplayValue(findFieldByHashCode(fields, "rpnummer"));
    return [
        rpnummer ? `RP Nr. ${rpnummer}` : "",
        formTypeLabel(payload.form && payload.form.name),
        getProtocolLocationLabel(payload),
    ]
        .filter((s) => s && s.trim() !== "")
        .join(" ")
        .trim();
}

/**
 * Extracts inventory items from multiple form payloads
 * Aggregates items by short_name per form type (Türliste groups first,
 * remaining types in order of first appearance)
 *
 * @param formPayloads - Array of form payload objects
 * @returns IExtractedItemsResult containing aggregated items and total count
 */
export function extractInventoryItemsFromForms(
    formPayloads: IFormPayload[],
    options: { referenceDate?: Date } = {}
): IExtractedItemsResult {
    const referenceDate = options.referenceDate || new Date();
    const groups = new Map<
        string,
        {
            formType: string;
            isTurListe: boolean;
            items: Map<string, IExtractedInventoryItem>;
        }
    >();
    const formLocations: string[] = [];
    const formEntryNames: string[] = [];
    const formProtocolLabels: string[] = [];
    const alignments: ITableAlignment[] = [];

    formPayloads.forEach((payload, formIndex) => {
        const fields = payload.fields || [];
        formLocations[formIndex] = getProtocolLocationLabel(payload);
        formEntryNames[formIndex] = payload.entryName || "";

        const formType = (payload.form && payload.form.name) || "";
        formProtocolLabels[formIndex] = isTurListeFormName(formType)
            ? ""
            : getProtocolShortLabel(payload);
        if (!groups.has(formType)) {
            groups.set(formType, {
                formType,
                isTurListe: isTurListeFormName(formType),
                items: new Map<string, IExtractedInventoryItem>(),
            });
        }
        const itemMap = groups.get(formType)!.items;

        // Find all InventoryItemSelectField fields
        const selectFields = findFieldsByType(fields, ["InventoryItemSelectField"]);

        selectFields.forEach(({ field }) => {
            const rawValue = field.value;
            let values: any[] = [];

            if (typeof rawValue === "string") {
                try {
                    const parsed = JSON.parse(rawValue);
                    if (Array.isArray(parsed)) {
                        values = parsed;
                    } else {
                        values = rawValue
                            .split(",")
                            .map((v) => v.trim())
                            .filter((v) => v);
                    }
                } catch (e) {
                    values = rawValue
                        .split(",")
                        .map((v) => v.trim())
                        .filter((v) => v);
                }
            } else if (Array.isArray(rawValue)) {
                values = rawValue;
            }

            values.forEach((val: any) => {
                const cellData = val?.cell || val;
                const shortName =
                    typeof val === "string"
                        ? val
                        : cellData?.short_name ||
                          cellData?.value ||
                          cellData?.name ||
                          cellData?.label ||
                          "";
                const name =
                    typeof val === "object"
                        ? cellData?.name || cellData?.label || shortName
                        : shortName;
                // const description = typeof val === "object" ? cellData?.description || "" : "";
                const salesPrice =
                    typeof cellData?.sales_price === "number"
                        ? cellData.sales_price
                        : typeof cellData?.salesPrice === "number"
                        ? cellData.salesPrice
                        : 0;

                if (!shortName) return;

                if (!itemMap.has(shortName)) {
                    itemMap.set(shortName, {
                        id: typeof cellData?.id === "number" ? cellData.id : null,
                        clientUuid: clientUuidOf(cellData),
                        shortName,
                        name,
                        description: "",
                        salesPrice,
                        count: 0,
                        totalQty: 0,
                        sources: [],
                        tableRowHints: [],
                        formType,
                    });
                }

                const item = itemMap.get(shortName)!;
                if (!item.id && typeof cellData?.id === "number") {
                    item.id = cellData.id;
                }
                if (!item.clientUuid) {
                    item.clientUuid = clientUuidOf(cellData);
                }
                item.count += 1;
                item.totalQty += 1;
                item.sources.push({
                    formIndex,
                    fieldType: field.type,
                    fieldHashCode: field.hashCode || "",
                });
            });
        });

        // Find all TableField fields that contain InventoryItemSelectTableRow
        const tableFields = findFieldsByType(fields, ["TableField"]);

        tableFields.forEach(({ field }) => {
            const tableItems = extractItemsFromTable(field, formIndex, alignments);

            tableItems.forEach((tableItem) => {
                const {
                    id,
                    clientUuid,
                    shortName,
                    name,
                    description,
                    salesPrice,
                    sources,
                    tableRowHints,
                    count,
                    totalQty,
                } = tableItem;
                if (!itemMap.has(shortName)) {
                    itemMap.set(shortName, {
                        id,
                        clientUuid,
                        shortName,
                        name,
                        description,
                        salesPrice,
                        count: 0,
                        totalQty: 0,
                        sources: [],
                        tableRowHints: [],
                        formType,
                    });
                }

                const item = itemMap.get(shortName)!;
                // Set id if not already set and present
                if (!item.id && id) item.id = id;
                if (!item.clientUuid && clientUuid) item.clientUuid = clientUuid;
                item.count += count || 1;
                item.totalQty += totalQty || 1;
                // Update with first available full data if not already set
                if (!item.name && name) item.name = name;
                if (!item.description && description) item.description = description;
                if (!item.salesPrice && salesPrice) item.salesPrice = salesPrice;
                if (sources && sources.length > 0) {
                    item.sources.push(...sources);
                }
                if (tableRowHints && tableRowHints.length > 0) {
                    item.tableRowHints.push(...tableRowHints);
                }
            });
        });

        const dueByItem = new Map<number, IDueHit[]>();
        collectDueHits(fields, referenceDate).forEach((hit) => {
            const list = dueByItem.get(hit.config.inventoryItemId) || [];
            list.push(hit);
            dueByItem.set(hit.config.inventoryItemId, list);
        });
        dueByItem.forEach((hits) => {
            const config = hits[0].config;
            let item = Array.from(itemMap.values()).find(
                (candidate) => numericInventoryId(candidate.id) === config.inventoryItemId
            );
            if (!item && config.shortName && itemMap.has(config.shortName)) {
                item = itemMap.get(config.shortName);
            }
            const manualCount = item
                ? item.sources.filter((source) => source.formIndex === formIndex).length
                : 0;
            const extra = hits.slice(manualCount);
            if (extra.length === 0) {
                return;
            }
            if (!item) {
                const shortName = config.shortName || config.name || String(config.inventoryItemId);
                item = {
                    id: config.inventoryItemId,
                    clientUuid: null,
                    shortName,
                    name: config.name || shortName,
                    description: "",
                    salesPrice: 0,
                    count: 0,
                    totalQty: 0,
                    sources: [],
                    tableRowHints: [],
                    formType,
                };
                itemMap.set(shortName, item);
            }
            if (!item.id) {
                item.id = config.inventoryItemId;
            }
            item.count += extra.length;
            item.totalQty += extra.length;
            extra.forEach((hit) => {
                item!.sources.push({
                    formIndex,
                    fieldType: "DueInventoryItem",
                    fieldHashCode: hit.fieldHashCode,
                    tableRowIndex: hit.tableRowIndex,
                    due: `hergestellt ${hit.manufactured}, fällig ${hit.expires}`,
                });
            });
        });
    });

    const groupKeys = Array.from(groups.keys()).filter((key) => groups.get(key)!.items.size > 0);
    const orderedKeys = groupKeys
        .filter((key) => groups.get(key)!.isTurListe)
        .concat(groupKeys.filter((key) => !groups.get(key)!.isTurListe));

    const items = orderedKeys.reduce(
        (acc: IExtractedInventoryItem[], key) =>
            acc.concat(Array.from(groups.get(key)!.items.values())),
        []
    );
    const totalCount = items.reduce((sum, item) => sum + item.count, 0);

    return {
        items,
        totalCount,
        formLocations,
        formEntryNames,
        formProtocolLabels,
        formTypeOrder: orderedKeys,
        alignments,
    };
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Converts extracted inventory items to InvoicePositionsListField data format
 * suitable for creating an Offer form
 *
 * @param extractedItems - Result from extractInventoryItemsFromForms
 * @param options - Optional configuration for the conversion
 * @returns Object compatible with InvoicePositionsListField data structure
 */
export function convertToOfferPositions(
    extractedItems: IExtractedItemsResult,
    options: {
        defaultUnit?: "repair" | "service" | "disposal" | "hours";
        defaultPrice?: number;
        includeHintsInDescription?: boolean;
        referenceDate?: Date;
    } = {}
): {
    invoicePositions: IInvoicePosition[];
    discount: number;
    vat: { value: number; label: string };
    shippingInCents: number;
    notice: string;
    notice2: string;
    processDateBegin: string | null;
    processDateEnd: string | null;
    processDateDelimiter: string;
    processWorkers: any[];
    unitSelection: { label: string; value: string };
} {
    const { defaultUnit = "repair", defaultPrice = 0, includeHintsInDescription = true } = options;

    const defaultVat = {
        value: 0.19,
        label: "19 % Mwst.",
    };

    const unitOptions = [
        { label: "Reparatur", value: "repair" },
        { label: "Service", value: "service" },
        { label: "Verkauf", value: "disposal" },
    ];

    const formLocations = extractedItems.formLocations || [];
    const formEntryNames = extractedItems.formEntryNames || [];
    const formProtocolLabels = extractedItems.formProtocolLabels || [];
    const labelForForm = (formIndex: number): string =>
        formProtocolLabels[formIndex] ||
        formLocations[formIndex] ||
        formEntryNames[formIndex] ||
        "";

    // Create invoice positions from extracted items
    const invoicePositions: IInvoicePosition[] = extractedItems.items.map((item, index) => {
        let description = item.description || "";

        if (includeHintsInDescription) {
            const doorsByForm = new Map<number, Set<number>>();
            for (const source of item.sources) {
                if (source.tableRowIndex == null) {
                    continue;
                }
                if (!doorsByForm.has(source.formIndex)) {
                    doorsByForm.set(source.formIndex, new Set<number>());
                }
                doorsByForm.get(source.formIndex)!.add(source.tableRowIndex + 1);
            }

            const labelCounts = new Map<string, number>();
            doorsByForm.forEach((doorNumbers, formIndex) => {
                const label = labelForForm(formIndex);
                labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
            });

            const doorLines: string[] = [];
            doorsByForm.forEach((doorNumbers, formIndex) => {
                let label = labelForForm(formIndex);
                if ((labelCounts.get(label) || 0) > 1 && formEntryNames[formIndex]) {
                    label = formEntryNames[formIndex];
                }
                const doors = Array.from(doorNumbers)
                    .sort((a, b) => a - b)
                    .map((n) => `Tür ${n}`)
                    .join(", ");
                doorLines.push(label ? `${label}: ${doors}` : doors);
            });

            const protocolLines: string[] = [];
            for (const source of item.sources) {
                if (source.tableRowIndex != null) {
                    continue;
                }
                const label =
                    formProtocolLabels[source.formIndex] || formEntryNames[source.formIndex] || "";
                if (label === "" || protocolLines.indexOf(label) !== -1) {
                    continue;
                }
                protocolLines.push(label);
            }

            const dueLines = item.sources
                .filter((source) => source.due)
                .map((source) =>
                    source.tableRowIndex == null
                        ? source.due!
                        : `Tür ${source.tableRowIndex + 1}: ${source.due}`
                );

            const lines = doorLines.concat(protocolLines, dueLines);
            if (lines.length > 0) {
                description = lines.map(escapeHtml).join("<br>");
            }
        }

        // Use extracted sales price if available, otherwise use default
        const price = item.salesPrice > 0 ? item.salesPrice : defaultPrice;

        return {
            nr: index + 1,
            title: item.name || item.shortName,
            description,
            qty: item.totalQty,
            unit: defaultUnit,
            price,
            inventory_item_id: numericInventoryId(item.id),
            inventory_item_client_uuid: item.clientUuid || null,
            in_stock: true,
        };
    });

    return {
        invoicePositions,
        discount: 0,
        vat: defaultVat,
        shippingInCents: 0,
        notice: "",
        notice2: "",
        processDateBegin: null,
        processDateEnd: null,
        processDateDelimiter: "bis",
        processWorkers: [],
        unitSelection: unitOptions[0],
    };
}

/**
 * Complete workflow: Extract items from forms and create Offer data
 * This is a convenience function that combines extraction and conversion
 *
 * @param formPayloads - Array of form payloads to process
 * @param options - Optional configuration for Offer creation
 * @returns Object with extraction results and offer data
 */
export function createOfferFromForms(
    formPayloads: IFormPayload[],
    options?: Parameters<typeof convertToOfferPositions>[1]
): {
    extraction: IExtractedItemsResult;
    offerData: ReturnType<typeof convertToOfferPositions>;
} {
    const extraction = extractInventoryItemsFromForms(formPayloads, {
        referenceDate: options && options.referenceDate,
    });
    const offerData = convertToOfferPositions(extraction, options);

    return {
        extraction,
        offerData,
    };
}
